import React, {useEffect, useRef, useState} from "react";
import {useLazyQuery, useMutation} from '@apollo/client';
import {
    Box,
    Button,
    Dialog,
    DialogContent,
    LinearProgress,
    Typography
} from "@mui/material";
import {grey} from '@mui/material/colors';
import moment from "moment-timezone";

import {useRefresh} from '../../contexts/RefreshContext';
import {FETCH_INTERNAL_TAGS_AND_BOOKING_INFO, SUBMIT_MEMBER_TAGS} from "../../queries/GraphQLQueries";
import {
    buildChildBiographicalDetails,
    buildTagViewModel,
    buildUniqueTagCategories,
} from "../../helpers/SessionHelper";
import AddTagsTable from "./AddTagsTable";
import CurrentTagsDisplay from "./CurrentTagsDisplay";

const COLORS = {
    BORDER_GRAY: grey[300],
};

const DIMENSIONS = {
    MAX_TAG_CONTAINER_HEIGHT_IN_PIXELS: 72,
};

const styles = {
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '0.25rem 1rem',
        borderBottom: `0.063rem solid ${COLORS.BORDER_GRAY}`
    },
    loadingBarContainer: {
        height: '0.25rem'
    },
    currentTagsContainer: (showAllTags) => ({
        marginBottom: '0.5rem',
        maxHeight: showAllTags ? 'none' : '3.5rem',
        overflow: showAllTags ? 'scrollbar' : 'hidden',
    }),
    sectionTitle: {
        marginBottom: '0.5rem',
        fontSize: '0.75rem',
        fontWeight: 600
    },
    dialogContent: {
        paddingX: '1rem',
        paddingTop: '0.25rem',
        paddingBottom: '0.5rem',
        display: 'flex',
        flexDirection: 'column',
        minHeight: '25rem',
    },
    buttonContainer: {
        textAlign: 'center'
    },
    button: {
        textTransform: 'none',
        fontSize: '0.875rem'
    }
};

export default function InternalTagsDialog({isTagsDialogOpen, handleCloseTagsDialog, child, tagMetrics, bookingId, onTagsUpdated, sessionStartTime}) {
    const {refreshTrigger} = useRefresh();
    const [availableTags, setAvailableTags] = useState([]);
    const [categories, setCategories] = useState([]);
    const [showAllTags, setShowAllTags] = useState(false);
    const [shouldShowSeeAllButton, setShouldShowSeeAllButton] = useState(false);
    const containerRef = useRef(null);

    const [submitMemberTags, {loading: submittingTags}] = useMutation(SUBMIT_MEMBER_TAGS);

    const [fetchTagsAndBookingInfo, {loading: allTagsLoading, data: internalTagsData, refetch}] = useLazyQuery(FETCH_INTERNAL_TAGS_AND_BOOKING_INFO);

    useEffect(() => {
        if (isTagsDialogOpen) {
            fetchTagsAndBookingInfo({variables: {bookingId}});
        } else {
            setShowAllTags(false);
        }
    }, [isTagsDialogOpen]);

    useEffect(() => {
        if (isTagsDialogOpen && refetch) {
            refetch();
        }
    }, [refreshTrigger, isTagsDialogOpen, refetch]);

    useEffect(() => {
        if (!internalTagsData) {
            return;
        }

        const selectedTagIds = internalTagsData.bookingInfo?.tags?.map(tag => tag.id) || [];

        if (internalTagsData.tags) {
            const transformedTags = internalTagsData.tags.map(tag => {
                const isSelected = selectedTagIds.includes(tag.id);
                const currentTagMetric = tagMetrics?.find(ts => ts.tag.id === tag.id);
                const tagVoteCount = currentTagMetric?.count || 0;
                return buildTagViewModel(tag, tagVoteCount, isSelected);
            });

            setAvailableTags(transformedTags);

            const uniqueCategories = buildUniqueTagCategories(internalTagsData.tags);
            setCategories(uniqueCategories);
        }
    }, [internalTagsData, tagMetrics]);

    const handleAddTag = async (selectedTag) => {
        if (!selectedTag) {
            return;
        }

        const currentlySelectedTagIds = availableTags
            .filter(tag => tag.isSelected)
            .map(tag => tag.id);

        let newVotedTagIds;

        if (selectedTag.isSelected) {
            newVotedTagIds = currentlySelectedTagIds.filter(id => id !== selectedTag.id);
        } else {
            newVotedTagIds = [...currentlySelectedTagIds, selectedTag.id];
        }

        await submitMemberTags({
            variables: {
                bookingId: bookingId,
                tags: newVotedTagIds
            }
        });

        refetch();
        if (onTagsUpdated) {
            onTagsUpdated();
        }
    };

    const currentTags = availableTags.filter(tag => tag.votes > 0);

    const isFutureSession = sessionStartTime ? moment(sessionStartTime).isAfter(moment()) : false;

    useEffect(() => {
        requestAnimationFrame(() => {
            if (!containerRef.current) {
                return;
            }

            const containerHeight = containerRef.current.scrollHeight;
            setShouldShowSeeAllButton(containerHeight > DIMENSIONS.MAX_TAG_CONTAINER_HEIGHT_IN_PIXELS);
        });
    }, [currentTags, isTagsDialogOpen]);

    return (
        <Dialog open={isTagsDialogOpen} onClose={handleCloseTagsDialog} maxWidth="md" fullWidth>
            <Box sx={styles.header}>
                <Typography variant="h6" fontWeight={600}>Child Tags</Typography>
                <Typography variant="body2" fontWeight='normal'>
                    {buildChildBiographicalDetails(child)}
                </Typography>
            </Box>
            <Box sx={styles.loadingBarContainer}>
                {submittingTags && <LinearProgress/>}
            </Box>
            <DialogContent sx={styles.dialogContent}>
                <Typography sx={styles.sectionTitle}>Current Tags</Typography>
                <Box ref={containerRef} sx={styles.currentTagsContainer(showAllTags)}>
                    <CurrentTagsDisplay
                        currentTags={currentTags}
                        isLoading={allTagsLoading}
                    />
                </Box>
                {
                    shouldShowSeeAllButton
                    &&
                    !showAllTags
                    &&
                    (
                        <Box sx={styles.buttonContainer}>
                            <Button
                              onClick={() => setShowAllTags(true)}
                              variant="text"
                              size="small"
                              sx={styles.button}>
                                See All
                            </Button>
                        </Box>
                    )}
                {
                    shouldShowSeeAllButton
                    &&
                    showAllTags
                    &&
                    (<Box sx={styles.buttonContainer}>
                            <Button
                              onClick={() => setShowAllTags(false)}
                              variant="text"
                              size="small"
                              sx={styles.button}>
                                Show Less
                            </Button>
                        </Box>
                    )}

                <AddTagsTable
                    availableTags={availableTags}
                    categories={categories}
                    onTagSelection={handleAddTag}
                    isFutureSession={isFutureSession}/>
            </DialogContent>
        </Dialog>
    );
}